// @ts-strict-ignore
import {apiClient} from 'common/util/api-client';
import {ApiTheme, ThemesRequest, ThemesResponse, ThemesResultsMap} from 'app_api/projects/themes/types';
import {ApiResourceClient} from 'common/util/model-resource/api-resource';
import {getProjectId} from 'common/report/util';
import {reporter} from 'common/report/error-reporting';
import {AtLeastOne} from 'common/util/typescript';

import {appApiUrl} from '../utils';
import {purgeFetchCache} from '../utils/fetch-cache';

/** An object that must include `colors` or `darkColors` (or both) fields, each an array of color strings */
type ThemeScheme = AtLeastOne<{colors?: string[]; darkColors?: string[]}>;
// schema4api unfortunately doesn't capture the type of `data` defined in
// `webapp/app_api/projects/themes/__types__/themes_req.py`
export type Theme = Omit<Omit<ApiTheme, 'data'>, 'id'> & {
  /** Static themes have a string ID */
  id?: string | number;
  data: ThemeScheme;
};

/** bookmarkParams.displayOptions.theme can be an inline Theme, a saved theme id (int), or a static theme id (string) */
export type BookmarkTheme = Theme | {id: string | number};

export type ThemesResultsMapType = ThemesResultsMap & {[k: string]: Theme};
export type ThemesRequestType = ThemesRequest & {data: ThemeScheme};

function buildEndpoint({themeId = null}: {themeId?: string} = {}): string {
  return appApiUrl(`themes` + (themeId ? `/${themeId}` : ``), {projectId: getProjectId()});
}

export async function createTheme(themesRequest: ThemesRequestType): Promise<ThemesResultsMapType> {
  try {
    return await apiClient.post<ThemesResultsMapType>(buildEndpoint(), themesRequest);
  } catch (error) {
    reporter.error(error, `createTheme error`, {themesRequest});
    throw error;
  }
}

export async function updateTheme({
  themeId,
  theme,
}: {
  themeId: string;
  theme: Partial<Theme>;
}): Promise<ThemesResultsMapType> {
  try {
    return await apiClient.patch<ThemesResultsMapType>(buildEndpoint({themeId}), theme);
  } catch (error) {
    reporter.error(error, `updateTheme error`, {themeId, theme});
    throw error;
  }
}

export async function deleteTheme({themeId}: {themeId: string}): Promise<ThemesResultsMapType> {
  try {
    return await apiClient.delete<ThemesResultsMapType>(buildEndpoint({themeId}));
  } catch (error) {
    reporter.error(error, `deleteTheme error`, {themeId});
    throw error;
  }
}

export const themesClient: ApiResourceClient<TODO> = {
  index() {
    return apiClient.hydratedGet<ThemesResponse>(buildEndpoint());
  },

  get(themeId: string) {
    return apiClient.hydratedGet<ThemesResponse>(buildEndpoint({themeId}));
  },

  purgeCache() {
    purgeFetchCache({everything: true, path: buildEndpoint()});
  },
};
